/**
 * Date-input plumbing.
 *
 * `<input type="date">` speaks `YYYY-MM-DD` in the user's local calendar.
 * Schemas store ISO 8601 UTC (§5). Converting naively — `new Date('2026-08-06')`
 * — parses as UTC midnight, which in US timezones is the *previous evening*,
 * so a receipt dated the 6th would be stored as the 5th and could land in the
 * wrong month on a reconciliation packet.
 *
 * These helpers pin the calendar date the user actually picked to noon UTC,
 * which is the same calendar day in every timezone from UTC−11 to UTC+11.
 */

/** `YYYY-MM-DD` from a date input to an ISO 8601 UTC timestamp. */
export function dateInputToIso(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match === null) return null
  const [, year, month, day] = match
  const date = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0, 0),
  )
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** ISO 8601 UTC back to the `YYYY-MM-DD` a date input expects. */
export function isoToDateInput(iso: string | null | undefined): string {
  if (iso == null || iso === '') return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

/** Today, as a date input value. */
export function todayInput(now: Date = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Short display date, e.g. `Aug 6, 2026`. */
export function formatDate(iso: string | null | undefined): string {
  if (iso == null || iso === '') return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Date and time, e.g. `Aug 6, 2026, 2:14 PM` — in the reader's own timezone.
 *
 * Unlike `formatDate`, which pins to UTC because a purchase date is a calendar
 * date and must not drift across midnight, this is a moment in time: "when did
 * this happen to them" is only answerable in local time.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (iso == null || iso === '') return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Month heading for grouped transaction lists, e.g. `August 2026`. */
export function formatMonth(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
